import { FC } from 'react';
import { Box, Text, TextButton } from '@wix/design-system';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { useExperiments, useTranslation } from '@wix/fe-essentials-standalone';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { HeartSmall } from '@wix/wix-ui-icons-common';
import {
  ENABLE_TEMPLATE_FAVORITES_SPEC,
  TEMPLATES_GALLERY_ENTRY,
} from '@utils/constants';
import { FAVORITES_BI_CLICK_TYPES } from '@components/Templates/Favorites/useFavoritesBi';
import { useTemplateFavoritesCount } from '@components/Templates/Favorites/useTemplateFavoritesCount';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import {
  redirectToLoginWithFavoritesPage,
  templatesFavoritesPath,
} from '@utils/urlUtils';
import ResetButton from '../ResetButton';
import s from './FilterPanelHeader.module.scss';

const FilterPanelHeader: FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { userDetails } = useHeaderContext();
  const { experiments } = useExperiments({ readOnly: true });
  const {
    state: { filterOpen },
  } = useTemplatesContext();
  const { logBiClick } = useTemplatesBi({});
  const isLoggedIn = Boolean(userDetails?.email);
  const shouldShowFavoritesLink =
    filterOpen && experiments.enabled(ENABLE_TEMPLATE_FAVORITES_SPEC);

  const { favoriteCount } = useTemplateFavoritesCount({
    enabled: shouldShowFavoritesLink && isLoggedIn,
  });

  return (
    <div
      className={s.headerWrapper}
      style={{
        borderBottomWidth: filterOpen ? '1px' : '0px',
      }}
    >
      {shouldShowFavoritesLink && (
        <Box borderBottom="1px solid #DFE5EB">
          <Box
            margin="18px 30px 18px 24px"
            gap="6px"
            width="100%"
            align="space-between"
          >
            <TextButton
              as="a"
              href={templatesFavoritesPath}
              onClick={
                !isLoggedIn
                  ? (e) => {
                      e.preventDefault();
                      redirectToLoginWithFavoritesPage(language);
                    }
                  : () =>
                      logBiClick({
                        click_type: FAVORITES_BI_CLICK_TYPES.favorites,
                        entry: TEMPLATES_GALLERY_ENTRY,
                      })
              }
              prefixIcon={<HeartSmall />}
              size="small"
              skin="dark"
              weight="normal"
              dataHook="favorites-link"
            >
              {t('marketplace.templates-page.favorites-link')}
            </TextButton>
            <Box>
              {favoriteCount > 0 && (
                <Text size="small" skin="standard">
                  {favoriteCount}
                </Text>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <Box
        align="space-between"
        margin="0px 24px"
        verticalAlign="middle"
        padding="18px 0"
      >
        <Text weight="bold">
          {t('marketplace.templates-page.filters-title')}
        </Text>
        <ResetButton />
      </Box>
    </div>
  );
};

export default FilterPanelHeader;
